/**
 * MapGuidedTour — Interactive First-Use Discovery
 * 
 * Instead of a static slideshow that TELLS users about interactions,
 * this component SHOWS them by overlaying coaching prompts on the live map
 * and waiting for users to perform the actual gestures.
 * 
 * Flow (when map has data):
 *   Step 0: welcome       — "Welcome to the Cosmos" (centered card)
 *   Step 1: clickNode     — "Tap a star" (waits for user to click a project)
 *   Step 2: seeLineage    — "See the connections" (explains what just happened)
 *   Step 3: clickLine     — "Pluck a connection" (waits for user to click a line)
 *   Step 4: seeScrubber   — "Walk through time" (explains the scrubber)
 *   Step 5: doubleClick   — "Double-click to dive in" (teaches detail overlay)
 *   Step 6: ready         — "You're ready" (send-off)
 * 
 * Flow (empty map):
 *   Step 0: welcome → Step 5: doubleClick → Step 6: ready
 * 
 * Place at: src/components/map/MapGuidedTour.jsx
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Sparkles, MousePointer, Zap, ArrowRight,
  ChevronRight, X, Rocket, Eye, Hand, GitFork
} from 'lucide-react';

// ============================================================================
// STORAGE — tracks completion per user
// ============================================================================

const STORAGE_KEY = 'red-social-guided-tour-v2';

export function useTourState(_userId) {
  // Use a stable browser-level key — the tour is a one-time per-browser
  // experience, not per-account. Tying it to userId caused a race where
  // userId arrived async and the check fired with undefined, writing the
  // wrong key and making the tour re-appear on every login.
  const [hasCompleted, setHasCompleted] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });

  const complete = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setHasCompleted(true);
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setHasCompleted(false);
  }, []);

  return { showTour: !hasCompleted, completeTour: complete, resetTour: reset };
}

// ============================================================================
// STEP DEFINITIONS
// ============================================================================

const STEPS = {
  welcome: {
    id: 'welcome',
    icon: Sparkles,
    title: 'Welcome to the Cosmos',
    body: 'Every circle is a student project. Every line is an idea that inspired something new. Let\u2019s explore together.',
    cta: 'Show me',
    color: '#8b5cf6',
    position: 'center',
    overlay: 'full',
  },
  clickNode: {
    id: 'clickNode',
    icon: MousePointer,
    title: 'Tap any star',
    body: 'Click a project node to light up its creative lineage \u2014 everything it inspired, and everything that inspired it.',
    hint: 'Go ahead, click one',
    color: '#06b6d4',
    position: 'top-right',
    overlay: 'none',  // let user interact freely
    waitFor: 'project-click',
  },
  seeLineage: {
    id: 'seeLineage',
    icon: Eye,
    title: 'Beautiful.',
    body: 'See how the connections lit up? Cross-discipline links glow extra bright \u2014 that\u2019s a poem becoming code, a photo becoming a sculpture. That\u2019s the magic.',
    cta: 'What else?',
    color: '#ec4899',
    position: 'bottom-center',
    overlay: 'none',
    delay: 500,
  },
  clickLine: {
    id: 'clickLine',
    icon: Zap,
    title: 'Now pluck a line',
    body: 'Click any of the glowing connection lines. It\u2019ll vibrate like a guitar string and open a timeline of the full creative journey.',
    hint: 'Click a bright line',
    color: '#f59e0b',
    position: 'bottom-center',
    overlay: 'none',
    waitFor: 'scrubber-open',
  },
  seeScrubber: {
    id: 'seeScrubber',
    icon: Hand,
    title: 'The lineage scrubber',
    body: 'Swipe through to see how one idea evolved. A recipe that became a typeface. A melody that became a building. This is the story the map tells.',
    cta: 'Love it',
    color: '#22c55e',
    position: 'top-center',
    overlay: 'none',
    delay: 300,
  },
  doubleClick: {
    id: 'doubleClick',
    icon: GitFork,
    title: 'Double-click to open',
    body: 'Double-click any project to see the full details \u2014 files, comments, fork history. From there, fork it to add your own spin, or answer a challenge.',
    cta: 'Got it',
    color: '#8b5cf6',
    position: 'center',
    overlay: 'dim',
  },
  ready: {
    id: 'ready',
    icon: Rocket,
    title: 'You\u2019re ready',
    body: 'Explore freely. Fork what inspires you. Answer challenges. Every project you create becomes a new star for someone else to discover.',
    cta: 'Enter the Cosmos',
    color: '#ec4899',
    position: 'center',
    overlay: 'full',
    isFinal: true,
  },
};

const STEP_ORDER_FULL = ['welcome', 'clickNode', 'seeLineage', 'clickLine', 'seeScrubber', 'doubleClick', 'ready'];
const STEP_ORDER_EMPTY = ['welcome', 'doubleClick', 'ready'];

// ============================================================================
// COACH TOOLTIP — the floating instruction card
// ============================================================================

const CoachTooltip = ({ step, onAction, onSkip, stepIdx, totalSteps }) => {
  const Icon = step.icon;
  const [visible, setVisible] = useState(false);

  // Fade in with optional delay
  useEffect(() => {
    setVisible(false);
    const timer = setTimeout(() => setVisible(true), step.delay || 80);
    return () => clearTimeout(timer);
  }, [step.id]);

  // Keyboard: Enter = advance, Escape = skip
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onSkip?.();
      if (e.key === 'Enter' && step.cta) onAction?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [step.id, step.cta, onAction, onSkip]);

  const positionClass = {
    'center': 'fixed inset-0 flex items-center justify-center',
    'bottom-center': 'fixed bottom-28 left-0 right-0 flex justify-center',
    'top-center': 'fixed top-20 left-0 right-0 flex justify-center',
    'top-right': 'fixed top-20 right-6 flex justify-end',
  }[step.position] || 'fixed inset-0 flex items-center justify-center';

  const progress = ((stepIdx + 1) / totalSteps) * 100;

  return (
    <div
      className={`${positionClass} z-[260] pointer-events-none`}
      style={{ padding: '0 20px' }}
    >
      <div
        className="pointer-events-auto"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.97)',
          transition: 'all 0.45s cubic-bezier(0.19, 1, 0.22, 1)',
          maxWidth: '380px',
          width: '100%',
        }}
      >
        <div
          className="relative backdrop-blur-2xl border rounded-2xl overflow-hidden"
          style={{
            backgroundColor: 'rgba(9, 9, 11, 0.94)',
            borderColor: `${step.color}25`,
            boxShadow: `0 0 100px ${step.color}08, 0 25px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)`,
          }}
        >
          {/* Progress */}
          <div className="h-[2px] bg-zinc-800/40">
            <div
              className="h-full rounded-full"
              style={{
                width: `${progress}%`,
                backgroundColor: step.color,
                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </div>

          <div className="p-5">
            {/* Icon + text */}
            <div className="flex items-start gap-3.5">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${step.color}, ${step.color}88)`,
                  boxShadow: `0 4px 24px ${step.color}25`,
                }}
              >
                <Icon className="w-5 h-5 text-white" strokeWidth={1.8} />
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-[15px] font-bold text-white mb-1.5 leading-snug">
                  {step.title}
                </h3>
                <p className="text-[13px] text-zinc-400 leading-relaxed">
                  {step.body}
                </p>
              </div>
            </div>

            {/* Action row */}
            <div className="mt-4 flex items-center justify-between">
              <button
                onClick={onSkip}
                className="text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors px-1"
              >
                Skip tour
              </button>

              {step.cta ? (
                <button
                  onClick={onAction}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold text-white transition-all duration-200 hover:brightness-110 active:scale-[0.97]"
                  style={{
                    background: `linear-gradient(135deg, ${step.color}, ${step.color}aa)`,
                    boxShadow: `0 4px 20px ${step.color}25`,
                  }}
                >
                  {step.isFinal ? (
                    <><span>{step.cta}</span> <Rocket className="w-3.5 h-3.5" /></>
                  ) : (
                    <><span>{step.cta}</span> <ChevronRight className="w-3.5 h-3.5" /></>
                  )}
                </button>
              ) : step.waitFor ? (
                <div className="flex items-center gap-2 text-[12px] text-zinc-500 pr-1">
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{
                      backgroundColor: step.color,
                      animation: 'tourDotPulse 1.4s ease-in-out infinite',
                    }}
                  />
                  {step.hint || 'Waiting\u2026'}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Step counter */}
        <div className="flex justify-center mt-3 gap-1.5">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className="h-1 rounded-full transition-all duration-300"
              style={{
                width: i === stepIdx ? 20 : 6,
                backgroundColor: i === stepIdx ? step.color : 'rgba(113, 113, 122, 0.3)',
              }}
            />
          ))}
        </div>
      </div>

      {/* Keyframe for waiting dot pulse */}
      <style>{`
        @keyframes tourDotPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.8); }
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// MAIN GUIDED TOUR COMPONENT
// ============================================================================

const MapGuidedTour = ({
  onComplete,
  // Map data — used to decide full vs. empty tour
  projects = [],
  connections = [],
  // Current map state — the tour watches these to detect user actions
  selectedProjectId,
  showScrubber,
  highlightedIds = [],
}) => {
  const [stepIdx, setStepIdx] = useState(0);
  const prevSelectedRef = useRef(null);
  const prevScrubberRef = useRef(false);

  // Decide which step sequence to use
  const hasData = projects.length >= 2 && connections.length >= 1;
  const stepOrder = hasData ? STEP_ORDER_FULL : STEP_ORDER_EMPTY;
  const currentStepId = stepOrder[stepIdx];
  const currentStep = STEPS[currentStepId];
  const totalSteps = stepOrder.length;

  // ── Advance to next step ─────────────────────────────────────────
  const advance = useCallback(() => {
    setStepIdx(prev => {
      const next = prev + 1;
      if (next >= stepOrder.length) {
        onComplete?.();
        return prev;
      }
      return next;
    });
  }, [stepOrder.length, onComplete]);

  const skip = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  // ── Watch for user actions to auto-advance "waitFor" steps ──────
  useEffect(() => {
    if (!currentStep?.waitFor) return;

    if (currentStep.waitFor === 'project-click') {
      if (selectedProjectId && selectedProjectId !== prevSelectedRef.current) {
        prevSelectedRef.current = selectedProjectId;
        // Let the lineage animation play, then advance
        const timer = setTimeout(advance, 400);
        return () => clearTimeout(timer);
      }
    }

    if (currentStep.waitFor === 'scrubber-open') {
      if (showScrubber && !prevScrubberRef.current) {
        prevScrubberRef.current = true;
        const timer = setTimeout(advance, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [currentStep?.waitFor, selectedProjectId, showScrubber, advance]);

  // Reset scrubber tracking when we leave the scrubber step
  useEffect(() => {
    if (currentStepId !== 'clickLine') {
      prevScrubberRef.current = false;
    }
  }, [currentStepId]);

  if (!currentStep) return null;

  // ── Determine overlay behavior ──────────────────────────────────
  const showFullOverlay = currentStep.overlay === 'full';
  const showDimOverlay = currentStep.overlay === 'dim';

  return (
    <>
      {/* Full dark overlay — blocks interaction (welcome, final) */}
      {showFullOverlay && (
        <div
          className="fixed inset-0 z-[250] bg-black/75 backdrop-blur-sm"
          style={{ animation: 'tourOverlayIn 0.4s ease-out' }}
        />
      )}

      {/* Dim overlay — softer, for info steps */}
      {showDimOverlay && (
        <div
          className="fixed inset-0 z-[250] bg-black/50"
          style={{ animation: 'tourOverlayIn 0.3s ease-out' }}
        />
      )}

      {/* Coach tooltip */}
      <CoachTooltip
        step={currentStep}
        stepIdx={stepIdx}
        totalSteps={totalSteps}
        onAction={() => {
          if (currentStep.isFinal) {
            onComplete?.();
          } else {
            advance();
          }
        }}
        onSkip={skip}
      />

      <style>{`
        @keyframes tourOverlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default MapGuidedTour;